// pages/waste/WasteAnalytics.jsx
// Role-aware: farmers see their request stats; collectors see their bid + collection stats.
import { useEffect, useMemo, useState } from 'react';
import { Doughnut, Line, Bar } from 'react-chartjs-2';
import {
  FaTrash,
  FaCheckCircle,
  FaHourglassHalf,
  FaGavel,
  FaBoxes,
  FaDollarSign,
  FaPercent,
  FaStar,
  FaTrophy,
} from 'react-icons/fa';
import analyticsApi from '../../../services/analyticsApi';
import {
  StatCard,
  ChartCard,
  doughnutOptions,
  baseBarOptions,
  lineOptions,
  Pagination,
  WASTE_STATUS_COLORS,
  wasteStatusDoughnutData,
  BID_STATUS_COLORS,
} from '../analytics/analyticsShared';

const PAGE_SIZE = 8;

const TYPE_COLORS = ['#1e5c33', '#7caf64', '#f59e0b', '#3b82f6', '#8b5cf6', '#ef4444'];

export default function WasteAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [breakdownPage, setBreakdownPage] = useState(1);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await analyticsApi.getMyWaste();
        if (mounted) setData(res.analytics);
      } catch {
        /* interceptor handles toasts */
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const pagedBreakdown = useMemo(() => {
    if (!data?.breakdown) return [];
    const start = (breakdownPage - 1) * PAGE_SIZE;
    return data.breakdown.slice(start, start + PAGE_SIZE);
  }, [data, breakdownPage]);

  if (loading) return <div className="flex justify-center items-center h-64 text-gray-500">Loading analytics…</div>;
  if (!data)   return <div className="text-center text-gray-500 mt-10">No waste analytics data available.</div>;

  const isFarmer    = data.viewerRole === 'farmer';
  const isCollector = data.viewerRole === 'waste_collector';

  const monthlyData = {
    labels: data.monthly.map((m) => m.label),
    datasets: [
      {
        label: isFarmer ? 'Requests' : 'Bids placed',
        data: data.monthly.map((m) => m.count),
        borderColor: '#1e5c33',
        backgroundColor: 'rgba(30,92,51,0.12)',
        borderWidth: 2,
        tension: 0.35,
        fill: true,
        pointRadius: 3,
      },
    ],
  };

  const typeData = {
    labels: data.byType.map((t) => t._id || 'Unknown'),
    datasets: [
      {
        label: 'Requests',
        data: data.byType.map((t) => t.count),
        backgroundColor: data.byType.map((_, i) => TYPE_COLORS[i % TYPE_COLORS.length]),
        borderRadius: 3,
      },
    ],
  };

  const bidDoughnutData = isCollector
    ? {
        labels: ['Pending', 'Accepted', 'Declined', 'Rejected', 'Completed'],
        datasets: [
          {
            data: [
              data.bidStatus.pending,
              data.bidStatus.accepted,
              data.bidStatus.declined,
              data.bidStatus.rejected,
              data.bidStatus.completed,
            ],
            backgroundColor: Object.values(BID_STATUS_COLORS),
            borderWidth: 0,
          },
        ],
      }
    : null;

  return (
    <div className="p-1 sm:p-3 space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-primary">
          {isFarmer ? 'My Waste Request Analytics' : 'My Collection Analytics'}
        </h1>
        <p className="text-sm text-gray-500">
          {isFarmer
            ? 'Overview of all waste requests you have submitted'
            : 'Your bidding performance and assigned collection metrics'}
        </p>
      </div>

      {/* Farmer stat cards */}
      {isFarmer && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard label="Total Requests" value={data.total}                                    icon={<FaTrash />} />
          <StatCard label="Open"           value={data.open}                                     icon={<FaHourglassHalf />} accent="text-amber-600" />
          <StatCard label="Completed"      value={data.status.completed}                         icon={<FaCheckCircle />}   accent="text-primary" />
          <StatCard label="Total Quantity" value={`${data.totalQuantity} kg`}                    icon={<FaBoxes />}         accent="text-blue-600" />
          <StatCard label="Est. Value"     value={`$${data.estimatedValue.toLocaleString()}`}    icon={<FaDollarSign />}    accent="text-primary" />
          <StatCard label="Completion"     value={`${data.completionRate}%`}                     icon={<FaPercent />}       accent="text-primary" />
        </div>
      )}

      {/* Collector stat cards */}
      {isCollector && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard label="Total Bids"  value={data.totalBids}                                   icon={<FaGavel />} />
          <StatCard label="Won Bids"    value={data.wonBids}                                     icon={<FaTrophy />}        accent="text-primary" />
          <StatCard label="Win Rate"    value={`${data.winRate}%`}                               icon={<FaPercent />}       accent="text-primary" />
          <StatCard label="Earnings"    value={`$${(data.totalEarnings || 0).toLocaleString()}`} icon={<FaDollarSign />}    accent="text-primary" />
          <StatCard label="Active"      value={data.activeCollections}                           icon={<FaHourglassHalf />} accent="text-amber-600" />
          <StatCard label="Completion"  value={`${data.completionRate}%`}                        icon={<FaCheckCircle />}   accent="text-primary" />
        </div>
      )}

      {/* Charts row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <ChartCard title="Status Breakdown">
          <div style={{ height: 250 }}>
            <Doughnut
              data={isFarmer ? wasteStatusDoughnutData(data.status) : bidDoughnutData}
              options={doughnutOptions}
            />
          </div>
        </ChartCard>
        <ChartCard
          title={isFarmer ? 'Requests Over Time (6 months)' : 'Bids Over Time (6 months)'}
          className="lg:col-span-2"
        >
          <div style={{ height: 250 }}>
            <Line data={monthlyData} options={lineOptions} />
          </div>
        </ChartCard>
      </div>

      {/* Charts row 2 */}
      {data.byType?.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <ChartCard title="Waste by Type" className="lg:col-span-2">
            <div style={{ height: 220 }}>
              <Bar data={typeData} options={baseBarOptions} />
            </div>
          </ChartCard>
          <div />
        </div>
      )}

      {/* Counterparty breakdown table */}
      {data.breakdown?.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              {isFarmer ? 'Collector Breakdown' : 'Farmer Breakdown'}
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">{data.counterpartyLabel}</th>
                  {isFarmer && <th className="text-left px-3 py-2 font-medium">Rating</th>}
                  <th className="text-left px-3 py-2 font-medium">Total</th>
                  <th className="text-left px-3 py-2 font-medium">Accepted</th>
                  <th className="text-left px-3 py-2 font-medium">Completed</th>
                  {isFarmer && <th className="text-left px-3 py-2 font-medium">Qty Processed (kg)</th>}
                  <th className="text-left px-3 py-2 font-medium">Last Activity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pagedBreakdown.map((row) => (
                  <tr key={String(row.counterpartyId)} className="hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <div className="font-medium text-gray-800">{row.name || 'Unknown'}</div>
                      <div className="text-xs text-gray-400">{row.email || ''}</div>
                    </td>
                    {isFarmer && (
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center gap-1 text-amber-500">
                          <FaStar className="text-xs" />
                          <span className="text-gray-700">
                            {row.rating != null ? Number(row.rating).toFixed(1) : '—'}
                          </span>
                        </span>
                      </td>
                    )}
                    <td className="px-3 py-2 font-semibold text-gray-800">{row.total}</td>
                    <td className="px-3 py-2" style={{ color: WASTE_STATUS_COLORS.accepted }}>{row.accepted}</td>
                    <td className="px-3 py-2" style={{ color: WASTE_STATUS_COLORS.completed }}>{row.completed}</td>
                    {isFarmer && (
                      <td className="px-3 py-2 text-gray-700">{row.quantityProcessed || 0}</td>
                    )}
                    <td className="px-3 py-2 text-xs text-gray-500">
                      {row.lastActivity ? new Date(row.lastActivity).toLocaleDateString('en-GB') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={breakdownPage}
            setPage={setBreakdownPage}
            total={data.breakdown.length}
            pageSize={PAGE_SIZE}
          />
        </div>
      )}
    </div>
  );
}